// src/components/SimulationPanel.jsx
import React, { useState, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from "recharts";

function fmt(n, d = 2) {
  if (n == null || isNaN(n)) return "—";
  return n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}

export default function SimulationPanel({ holdings, marketData, exchangeRates }) {
  const [simActions, setSimActions] = useState([]); // [{ ticker, action, quantity, newTicker, market, price }]
  const [newAction, setNewAction] = useState({
    type: "buy",       // buy | sell | new
    ticker: "",
    quantity: "",
    price: "",
    market: "US",
    name: "",
  });

  const totalValueUSD = holdings.reduce((sum, h) => {
    const md = marketData[h.ticker];
    return sum + (md ? md.priceUSD * h.quantity : 0);
  }, 0);

  // 시뮬레이션 적용 후 포트폴리오
  const simHoldings = useMemo(() => {
    const map = {};
    holdings.forEach(h => {
      const md = marketData[h.ticker];
      map[h.ticker] = {
        ticker: h.ticker,
        name: md?.name || h.name || h.ticker,
        market: h.market,
        quantity: h.quantity,
        priceUSD: md?.priceUSD || 0,
      };
    });

    simActions.forEach(a => {
      if (a.type === "buy") {
        if (map[a.ticker]) map[a.ticker].quantity += a.quantity;
        else map[a.ticker] = { ticker: a.ticker, name: a.ticker, market: a.market, quantity: a.quantity, priceUSD: a.price };
      } else if (a.type === "sell") {
        if (map[a.ticker]) {
          map[a.ticker].quantity -= a.quantity;
          if (map[a.ticker].quantity <= 0) delete map[a.ticker];
        }
      }
    });

    return Object.values(map);
  }, [holdings, marketData, simActions]);

  const simTotalUSD = simHoldings.reduce((sum, h) => sum + h.priceUSD * h.quantity, 0);

  // 기존 비중 vs 시뮬 비중 비교 데이터
  const chartData = useMemo(() => {
    const allTickers = new Set([
      ...holdings.map(h => h.ticker),
      ...simHoldings.map(h => h.ticker),
    ]);

    return Array.from(allTickers)
      .map(ticker => {
        const orig = holdings.find(h => h.ticker === ticker);
        const sim = simHoldings.find(h => h.ticker === ticker);
        const origMd = marketData[ticker];

        const origVal = orig && origMd ? origMd.priceUSD * orig.quantity : 0;
        const simVal = sim ? sim.priceUSD * sim.quantity : 0;

        const origWeight = totalValueUSD > 0 ? (origVal / totalValueUSD) * 100 : 0;
        const simWeight = simTotalUSD > 0 ? (simVal / simTotalUSD) * 100 : 0;
        const delta = simWeight - origWeight;

        return { ticker, origWeight, simWeight, delta };
      })
      .filter(d => d.origWeight > 0.1 || d.simWeight > 0.1)
      .sort((a, b) => b.origWeight - a.origWeight);
  }, [holdings, simHoldings, marketData, totalValueUSD, simTotalUSD]);

  const addSimAction = () => {
    if (!newAction.ticker || !newAction.quantity) return;
    const md = marketData[newAction.ticker];
    setSimActions(prev => [...prev, {
      ...newAction,
      quantity: +newAction.quantity,
      price: md?.priceUSD || +newAction.price || 0,
    }]);
    setNewAction(a => ({ ...a, ticker: "", quantity: "", price: "" }));
  };

  const removeAction = idx => {
    setSimActions(prev => prev.filter((_, i) => i !== idx));
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 8, padding: "10px 14px", fontSize: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
        {payload.map(p => (
          <div key={p.name} style={{ color: p.name === "현재" ? "#1a1a2e" : "#e67e22" }}>
            {p.name}: {fmt(p.value, 1)}%
          </div>
        ))}
        <div style={{ marginTop: 4, color: payload[1]?.value > payload[0]?.value ? "#0a7c59" : "#c0392b" }}>
          변화: {fmt(payload[1]?.value - payload[0]?.value, 1)}%p
        </div>
      </div>
    );
  };

  const existingTickers = holdings.map(h => h.ticker);

  return (
    <div style={{
      background: "#fff",
      border: "1px solid #e8e8e8",
      borderRadius: 12,
      padding: 20,
      marginBottom: 24,
    }}>
      <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: "#1a1a2e" }}>
        매수/매도 시뮬레이션
      </h3>

      {/* 액션 입력 */}
      <div style={{
        background: "#fafafa",
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        padding: 16,
        marginBottom: 16,
      }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <select
            value={newAction.type}
            onChange={e => setNewAction(a => ({ ...a, type: e.target.value }))}
            style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13, background: "#fff" }}
          >
            <option value="buy">매수</option>
            <option value="sell">매도</option>
          </select>

          {newAction.type === "buy" ? (
            <select
              value={newAction.ticker}
              onChange={e => setNewAction(a => ({ ...a, ticker: e.target.value }))}
              style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13, background: "#fff", minWidth: 120 }}
            >
              <option value="">종목 선택</option>
              {existingTickers.map(t => <option key={t} value={t}>{t}</option>)}
              <option value="__new__">+ 신규 종목</option>
            </select>
          ) : (
            <select
              value={newAction.ticker}
              onChange={e => setNewAction(a => ({ ...a, ticker: e.target.value }))}
              style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13, background: "#fff", minWidth: 120 }}
            >
              <option value="">종목 선택</option>
              {existingTickers.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          )}

          {newAction.ticker === "__new__" && (
            <>
              <input
                placeholder="티커 입력 (예: NVDA)"
                value={newAction.newTicker || ""}
                onChange={e => setNewAction(a => ({ ...a, ticker: e.target.value.toUpperCase(), newTicker: e.target.value.toUpperCase() }))}
                style={{ width: 100, padding: "6px 10px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13 }}
              />
              <select
                value={newAction.market}
                onChange={e => setNewAction(a => ({ ...a, market: e.target.value }))}
                style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13, background: "#fff" }}
              >
                <option value="US">🇺🇸 US</option>
                <option value="TW">🇹🇼 TW</option>
                <option value="JP">🇯🇵 JP</option>
                <option value="HK">🇭🇰 HK</option>
                <option value="KR">🇰🇷 KR</option>
              </select>
              <input
                placeholder="현재가 (USD)"
                type="number"
                value={newAction.price}
                onChange={e => setNewAction(a => ({ ...a, price: e.target.value }))}
                style={{ width: 100, padding: "6px 10px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13 }}
              />
            </>
          )}

          <input
            placeholder="수량"
            type="number"
            value={newAction.quantity}
            onChange={e => setNewAction(a => ({ ...a, quantity: e.target.value }))}
            style={{ width: 80, padding: "6px 10px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13 }}
          />

          {newAction.ticker && newAction.ticker !== "__new__" && marketData[newAction.ticker] && (
            <span style={{ fontSize: 12, color: "#888" }}>
              현재가: ${fmt(marketData[newAction.ticker]?.priceUSD, 2)}
              {newAction.quantity ? ` × ${newAction.quantity} = $${fmt(marketData[newAction.ticker]?.priceUSD * newAction.quantity, 0)}` : ""}
            </span>
          )}

          <button onClick={addSimAction} style={{
            padding: "6px 14px", borderRadius: 6, border: "none",
            background: "#1a1a2e", color: "#fff", fontSize: 13, cursor: "pointer", fontWeight: 600,
          }}>
            추가
          </button>
        </div>

        {/* 추가된 액션 목록 */}
        {simActions.length > 0 && (
          <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
            {simActions.map((a, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "4px 10px", borderRadius: 6,
                background: a.type === "buy" ? "#e8f5e9" : "#fce4ec",
                border: `1px solid ${a.type === "buy" ? "#a5d6a7" : "#f48fb1"}`,
                fontSize: 12,
              }}>
                <span style={{ color: a.type === "buy" ? "#2e7d32" : "#c62828", fontWeight: 700 }}>
                  {a.type === "buy" ? "▲" : "▼"} {a.ticker}
                </span>
                <span style={{ color: "#555" }}>{a.quantity}주</span>
                <button onClick={() => removeAction(i)} style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: "#aaa", fontSize: 12, padding: 0, lineHeight: 1,
                }}>✕</button>
              </div>
            ))}
            <button onClick={() => setSimActions([])} style={{
              padding: "4px 10px", borderRadius: 6, border: "1px solid #ddd",
              background: "#fff", fontSize: 12, cursor: "pointer", color: "#888",
            }}>
              초기화
            </button>
          </div>
        )}
      </div>

      {/* 비중 변화 차트 */}
      {simActions.length > 0 && (
        <>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 12,
            marginBottom: 16,
          }}>
            {[
              { label: "현재 총 평가", value: `$${fmt(totalValueUSD, 0)}` },
              { label: "시뮬 후 총 평가", value: `$${fmt(simTotalUSD, 0)}` },
              {
                label: "예상 변화",
                value: `${simTotalUSD - totalValueUSD >= 0 ? "+" : ""}$${fmt(simTotalUSD - totalValueUSD, 0)}`,
                color: simTotalUSD >= totalValueUSD ? "#0a7c59" : "#c0392b",
              },
            ].map(c => (
              <div key={c.label} style={{
                background: "#fafafa", borderRadius: 8, padding: "12px 16px",
                border: "1px solid #f0f0f0",
              }}>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>{c.label}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: c.color || "#1a1a2e" }}>{c.value}</div>
              </div>
            ))}
          </div>

          <div style={{ marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: "#888" }}>비중 변화 (종목별)</span>
          </div>
          <ResponsiveContainer width="100%" height={Math.max(chartData.length * 32, 200)}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 40 }}>
              <XAxis type="number" domain={[0, "auto"]} tickFormatter={v => `${v.toFixed(0)}%`} tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="ticker" tick={{ fontSize: 11 }} width={60} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="origWeight" name="현재" fill="#1a1a2e" barSize={10} />
              <Bar dataKey="simWeight" name="시뮬" fill="#e67e22" barSize={10} />
            </BarChart>
          </ResponsiveContainer>
        </>
      )}

      {simActions.length === 0 && (
        <div style={{ textAlign: "center", color: "#aaa", padding: "24px 0", fontSize: 13 }}>
          매수/매도 액션을 추가하면 비중 변화를 시뮬레이션할 수 있어요
        </div>
      )}
    </div>
  );
}
